/**
 * Sitewijde melding (banner boven de app) — KV via worker.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Breadcrumbs } from '../../components/Breadcrumbs';
import { SeoMeta } from '../../components/SeoMeta';
import { api } from '../../lib/api';

export function AdminMeldingen() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['admin-announcement'],
    queryFn: () => api.getAdminAnnouncement(),
  });

  const [savedFlash, setSavedFlash] = useState(false);
  const mut = useMutation({
    mutationFn: (body: { message: string; severity: string }) => api.putAdminAnnouncement(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-announcement'] });
      void qc.invalidateQueries({ queryKey: ['public-announcement'] });
      setSavedFlash(true);
      window.setTimeout(() => setSavedFlash(false), 4000);
    },
  });

  const current = data?.announcement;
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState<'info' | 'warning' | 'error'>('info');

  useEffect(() => {
    if (data === undefined) return;
    setMessage(current?.message ?? '');
    const s = current?.severity;
    setSeverity(s === 'warning' || s === 'error' ? s : 'info');
  }, [data, current]);

  return (
    <div>
      <SeoMeta
        path="/admin/meldingen"
        noindex
        title="Sitewijde melding"
        description="Beheer sitewijde banner en meldingen voor alle CalqTool™-gebruikers — platformbeheer alleen."
      />
      <Breadcrumbs items={[{ label: 'Platformbeheer', href: '/admin' }, { label: 'Sitewijde melding' }]} />
      <h1 className="mb-2 text-2xl font-bold text-text">Sitewijde melding</h1>
      <p className="mb-6 max-w-2xl text-sm text-text-muted">
        De tekst verschijnt bovenaan de site voor iedereen (inclusief niet-ingelogde bezoekers). Leeg maken en opslaan
        verwijdert de banner. Alleen ter informatie of onderhoud — geen juridische claims.
      </p>

      {isLoading ? (
        <p className="text-sm text-text-muted">Laden…</p>
      ) : (
        <form
          className="max-w-2xl space-y-4 rounded-xl border border-border bg-surface-card p-5"
          onSubmit={(e) => {
            e.preventDefault();
            void mut.mutateAsync({ message: message.trim(), severity });
          }}
        >
          <div>
            <label className="mb-1 block text-sm font-medium text-text">Niveau</label>
            <select
              value={severity}
              onChange={(e) => setSeverity(e.target.value as 'info' | 'warning' | 'error')}
              className="w-full max-w-xs rounded-lg border border-border px-3 py-2 text-text"
            >
              <option value="info">Informatie</option>
              <option value="warning">Waarschuwing</option>
              <option value="error">Belangrijk (rood)</option>
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-text">Bericht</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={5}
              maxLength={2000}
              className="w-full rounded-lg border border-border px-3 py-2 text-text"
              placeholder="Bijv. Gepland onderhoud zaterdag 10:00–12:00."
            />
            <p className="mt-1 text-xs text-text-muted">{message.length}/2000 tekens</p>
          </div>
          {current?.updated_at && (
            <p className="text-xs text-text-muted">Laatst bijgewerkt: {new Date(current.updated_at).toLocaleString('nl-NL')}</p>
          )}
          {mut.isError && (
            <p className="text-sm text-red-700 dark:text-red-300">
              {mut.error instanceof Error ? mut.error.message : 'Opslaan mislukt'}
            </p>
          )}
          {savedFlash && <p className="text-sm text-emerald-700 dark:text-emerald-300">Opgeslagen.</p>}
          <div className="flex flex-wrap gap-2">
            <button
              type="submit"
              disabled={mut.isPending}
              className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-alt disabled:opacity-50"
            >
              {mut.isPending ? 'Opslaan…' : 'Opslaan'}
            </button>
            <button
              type="button"
              disabled={mut.isPending}
              className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-text hover:bg-surface"
              onClick={() => {
                setMessage('');
                void mut.mutateAsync({ message: '', severity: 'info' });
              }}
            >
              Banner wissen
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
